import type { ReactNode } from 'react';
import { useOCRConfig } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

export function OCRSettingsPage() {
  const {
    data,
    form,
    loading,
    saving,
    error,
    setEnabled,
    setProvider,
    setClaimWorkers,
    setTaskPriority,
    updateTencentField,
    updatePaddleField,
    save,
    reload,
  } = useOCRConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-base font-medium text-gray-900">OCR 识别开关</h2>
            <p className="text-sm text-gray-500 mt-1">
              当前状态：{data?.ready ? '配置完整，可用于普通任务校验' : '当前启用的识别引擎配置未完成'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              启用 OCR 校验
            </label>
            <select
              value={form.provider}
              onChange={(e) => setProvider(e.target.value as typeof form.provider)}
              className={inputCls}
            >
              <option value="paddleocr">PaddleOCR 本地/内网服务</option>
              <option value="tencent">腾讯云 OCR（备用）</option>
            </select>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">识别任务调度</h2>
          <p className="text-sm text-gray-500 mt-1">普通任务与短信审核共用并发额度；识别引擎和审核规则仍分别按各自配置执行。</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="每个 API 进程的识别任务总并发数">
            <input
              type="number"
              min={1}
              max={8}
              step={1}
              value={form.normal_task_ocr_claim_workers}
              onChange={(e) => setClaimWorkers(Number(e.target.value))}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-gray-500">默认 1，范围 1～8。保存后约 5 秒内同步到各 API 进程，无需重启。</p>
          </Field>
          <Field label="任务优先级">
            <select
              aria-label="任务优先级"
              value={form.task_priority}
              onChange={(e) => setTaskPriority(e.target.value as typeof form.task_priority)}
              className={inputCls}
            >
              <option value="equal">同等优先（默认）</option>
              <option value="message">短信任务优先</option>
              <option value="normal">普通任务优先</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">同等优先时轮流处理；选择优先类型后会优先分配处理机会，同时避免另一类任务长期等待。</p>
          </Field>
        </div>
        <p className="text-xs leading-5 text-gray-500">设为 3 时，每个 API 的普通任务与短信任务合计最多同时处理 3 个；两个 API 合计最多 6 个。有一类队列为空时，另一类可以使用全部额度。调整并发或优先级不会中断正在处理的任务。</p>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">PaddleOCR</h2>
          <p className="text-sm text-gray-500 mt-1">
            当前状态：{data?.paddleocr?.ready ? '配置完整' : '配置未完成'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="服务地址">
            <input
              type="url"
              value={form.paddleocr.base_url}
              onChange={(e) => updatePaddleField('base_url', e.target.value)}
              className={inputCls}
              placeholder="http://10.0.0.10:19090"
            />
          </Field>
          <Field label="模型档位">
            <select
              value={form.paddleocr.model_tier}
              onChange={(e) => updatePaddleField('model_tier', e.target.value as typeof form.paddleocr.model_tier)}
              className={inputCls}
            >
              <option value="medium">PP-OCRv6 medium</option>
              <option value="small">PP-OCRv6 small</option>
              <option value="tiny">PP-OCRv6 tiny</option>
            </select>
          </Field>
          <Field label="每个工作进程的 CPU 线程数">
            <input
              type="number"
              min={1}
              max={32}
              step={1}
              value={form.paddleocr.cpu_threads}
              onChange={(e) => updatePaddleField('cpu_threads', Number(e.target.value))}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-gray-500">默认 3，范围 1～32；保存后在下一次识别请求生效。</p>
          </Field>
        </div>
        <p className="text-xs text-gray-500">工作进程数量由 OCR 服务部署配置决定，每个进程逐张识别。如需调整进程数量，请更新部署配置并重启 OCR 服务。</p>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">腾讯云 OCR（备用）</h2>
          <p className="text-sm text-gray-500 mt-1">
            当前状态：{data?.tencent.ready ? '配置完整' : '配置未完成'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="SecretId">
            <input
              type="text"
              value={form.tencent.secret_id}
              onChange={(e) => updateTencentField('secret_id', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="SecretKey">
            <input
              type="password"
              value={form.tencent.secret_key}
              onChange={(e) => updateTencentField('secret_key', e.target.value)}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Region">
            <input
              type="text"
              value={form.tencent.region}
              onChange={(e) => updateTencentField('region', e.target.value)}
              className={inputCls}
              placeholder="ap-guangzhou"
            />
          </Field>
          <Field label="Endpoint">
            <input
              type="text"
              value={form.tencent.endpoint}
              onChange={(e) => updateTencentField('endpoint', e.target.value)}
              className={inputCls}
              placeholder="ocr.tencentcloudapi.com"
            />
          </Field>
          <Field label="识别模式">
            <select
              value={form.tencent.mode}
              onChange={(e) => updateTencentField('mode', e.target.value as typeof form.tencent.mode)}
              className={inputCls}
            >
              <option value="accurate">高精度</option>
              <option value="fast">快速</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-500">
          <div>最近更新时间：{data?.updated_at ? formatDateTime(data.updated_at) : '未保存到数据库'}</div>
          <div>最近更新人：{data?.updated_by ?? '-'}</div>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? '保存中...' : '保存并立即生效'}
        </button>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
